import { useRef, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Printer, MessageCircle, CheckCircle2, Clock, PenLine } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { printDecision } from "../../lib/decisionPrint";
import { SignaturePad } from "../SignaturePad";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { EmpSelect } from "./LegalPickers";
import { PARTY_ROLES, fmt, waLink, errMsg } from "./legalUtils";

const summonsText = (c, p, company) => `السيد/ ${p.name} المحترم\nتدعوكم ${company?.name || "الشركة"} للحضور بصفتكم (${p.role}) بخصوص الحالة رقم ${c.number} «${c.title}»${p.date ? ` وذلك بتاريخ ${p.date.replace("T", " ")}` : ""}.\n${p.note || ""}`;

const Record = ({ c, p, onDone }) => {
  const pad = useRef(null);
  const [txt, setTxt] = useState("");
  const save = async () => {
    try { await api.legalAct(c.id, `parties/${p.id}/statement`, { statement: txt, signature: pad.current?.getSignature() || null }); toast.success("تم تسجيل الإفادة"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-2 space-y-2 rounded-xl bg-slate-50 p-2">
      <Textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={3} placeholder="نص الإفادة كما أدلى بها الطرف" data-testid={`legal-stmt-text-${p.id}`} />
      <SignaturePad ref={pad} height={110} />
      <Button size="sm" onClick={save} data-testid={`legal-stmt-save-${p.id}`}>حفظ الإفادة والتوقيع</Button>
    </div>
  );
};

const Party = ({ c, p, company, onChanged }) => {
  const [rec, setRec] = useState(false);
  const letter = () => printDecision({ number: c.number, title: "خطاب استدعاء", date: new Date().toISOString().slice(0, 10), employee_name: p.name,
    body_html: summonsText(c, p, company).split("\n").map((l) => `<p>${l}</p>`).join("") }, company, fileUrl);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`legal-party-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-sm">{p.name}</b><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold">{p.role}</span>
        <span className="text-[10px] text-slate-400">{p.kind === "employee" ? "موظف — أُشعر في تطبيقه" : `طرف خارجي ${p.phone || ""}`}</span>
        {p.status === "responded" ? <span className="ms-auto flex items-center gap-1 text-[11px] font-bold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> أدلى بإفادته {fmt(p.responded_at)}</span>
          : <span className="ms-auto flex items-center gap-1 text-[11px] text-amber-700"><Clock className="h-3.5 w-3.5" /> بانتظار الإفادة</span>}
      </div>
      {p.date && <p className="mt-1 text-[11px] text-slate-500">موعد الحضور: {p.date.replace("T", " ")}</p>}
      {p.statement && <blockquote className="mt-2 whitespace-pre-wrap border-r-4 border-amber-400 bg-amber-50/50 px-3 py-2 text-sm">{p.statement}</blockquote>}
      {p.signature_id && <img src={fileUrl(p.signature_id)} alt="توقيع" className="mt-1 h-14 object-contain" />}
      <div className="mt-2 flex flex-wrap gap-2">
        {p.kind === "external" && <>
          <Button size="sm" variant="outline" onClick={letter} className="h-8 gap-1" data-testid={`legal-party-letter-${p.id}`}><Printer className="h-3.5 w-3.5" /> خطاب استدعاء</Button>
          {p.phone && <a href={waLink(p.phone, summonsText(c, p, company))} target="_blank" rel="noreferrer" data-testid={`legal-party-wa-${p.id}`}
            className="inline-flex h-8 items-center gap-1 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"><MessageCircle className="h-3.5 w-3.5" /> واتساب</a>}
        </>}
        {c.can_act && p.status !== "responded" && <Button size="sm" variant="ghost" onClick={() => setRec(!rec)} className="h-8 gap-1" data-testid={`legal-party-record-${p.id}`}><PenLine className="h-3.5 w-3.5" /> تسجيل إفادته</Button>}
      </div>
      {rec && <Record c={c} p={p} onDone={() => { setRec(false); onChanged(); }} />}
    </div>
  );
};

export const LegalParties = ({ c, employees, onChanged }) => {
  const { company } = useAuth();
  const [f, setF] = useState({ kind: "employee", employee_id: "", name: "", phone: "", role: PARTY_ROLES[0], date: "", note: "" });
  const [open, setOpen] = useState(false);
  const add = async () => {
    try { await api.legalAct(c.id, "parties", f); toast.success("تم الاستدعاء"); setOpen(false); setF({ ...f, employee_id: "", name: "", phone: "", note: "" }); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      {(c.parties || []).map((p) => <Party key={p.id} c={c} p={p} company={company} onChanged={onChanged} />)}
      {!(c.parties || []).length && !open && <p className="py-6 text-center text-sm text-slate-400">لم يُستدعَ أي طرف</p>}
      {c.can_act && (open ? (
        <div className="space-y-2 rounded-2xl border-2 border-dashed border-slate-300 p-3" data-testid="legal-party-form">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-xs font-bold">
            {[["employee", "موظف من النظام"], ["external", "طرف خارجي"]].map(([k, l]) => <button key={k} onClick={() => setF({ ...f, kind: k })} data-testid={`legal-party-kind-${k}`} className={`flex-1 rounded-md py-1.5 ${f.kind === k ? "bg-white shadow" : "text-slate-500"}`}>{l}</button>)}
          </div>
          {f.kind === "employee" ? <EmpSelect employees={employees} value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} testId="legal-party-emp" />
            : <div className="grid grid-cols-2 gap-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="الاسم" data-testid="legal-party-name" /><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="الجوال 05xxxxxxxx" dir="ltr" data-testid="legal-party-phone" /></div>}
          <div className="grid grid-cols-2 gap-2">
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="h-9 rounded-md border px-2 text-sm" data-testid="legal-party-role">{PARTY_ROLES.map((r) => <option key={r}>{r}</option>)}</select>
            <Input type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="legal-party-date" />
          </div>
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="ملاحظة / المطلوب منه" />
          <div className="flex gap-2"><Button size="sm" onClick={add} data-testid="legal-party-submit">استدعاء</Button><Button size="sm" variant="outline" onClick={() => setOpen(false)}>إلغاء</Button></div>
        </div>
      ) : <Button variant="outline" onClick={() => setOpen(true)} className="w-full gap-1.5" data-testid="legal-party-add"><UserPlus className="h-4 w-4" /> استدعاء طرف</Button>)}
    </div>
  );
};
